/**
 * Client-side error telemetry.
 *
 * Boires is a static SPA: cart and checkout calls go straight from the browser
 * to the Portaus CRM, so a customer-side failure leaves no trace on our server
 * unless we send it there ourselves. This module collects those failures and
 * beacons them to the intake API, and hands back a short reference code the
 * customer can quote so a report can be matched to an exact event.
 */

// Ambiguous glyphs (O/0, I/1) are left out — these codes get read over the
// phone and retyped into emails.
const REF_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const MAX_BREADCRUMBS = 25;
const MAX_EVENTS_PER_SESSION = 20;
const DEDUPE_WINDOW_MS = 5000;
const MAX_STRING_LENGTH = 800;
// 25 breadcrumbs at full string length would blow past the intake API's 16kb
// JSON limit and the whole report would be rejected with a 413.
const MAX_BREADCRUMB_LENGTH = 200;
const MAX_DEPTH = 4;

const BEACON_PATH = '/errors/report';
const SESSION_STORAGE_KEY = 'boires-diag-session';

/**
 * Crawlers routinely abort script and prefetch loads to save crawl budget,
 * which surfaces as RESOURCE_LOAD_FAILED / network errors that no human ever
 * experienced. In the first two minutes after launch three of four reports came
 * from Googlebot and Bingbot, so they are excluded outright.
 */
const BOT_PATTERN = new RegExp([
  'bot', 'crawl', 'spider', 'slurp', 'archiver', 'headlesschrome', 'phantomjs',
  'lighthouse', 'pagespeed', 'gtmetrix', 'pingdom', 'facebookexternalhit',
  'whatsapp', 'telegrambot', 'preview', 'embedly', 'feedfetcher',
].join('|'), 'i');

function isCrawler() {
  try {
    if (navigator.webdriver) return true;
    return BOT_PATTERN.test(navigator.userAgent || '');
  } catch (e) {
    return false;
  }
}

/**
 * Only these keys survive redaction. Everything else becomes '[redacted]'.
 * An allowlist rather than a blocklist: cart and checkout payloads carry names,
 * emails and postal addresses, and a blocklist silently leaks whatever field
 * someone adds next.
 */
const SAFE_KEYS = [
  'amount', 'available', 'cartId', 'cartProductQty', 'code', 'companyId',
  'currency', 'deliveryBranch', 'detail', 'details', 'error', 'errorCode',
  'errors', 'erpProduct', 'id', 'inventory', 'inventoryCode', 'kind', 'locale',
  'market', 'message', 'method', 'name', 'paymentIntentId', 'pid',
  'portausCompanyId', 'productId', 'puid', 'quantity', 'qty', 'qtyAvailable',
  'reason', 'region', 'regionCode', 'shoppingCartId', 'status', 'statusCode',
  'statusText', 'subtotal', 'title', 'total', 'type', 'url', 'vintage',
];

// Applied to every string that survives the allowlist, in case an API echoes a
// customer's details back inside an error message.
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const LONG_DIGITS_PATTERN = /\b\d{7,}\b/g;

const state = {
  installed: false,
  sessionId: null,
  breadcrumbs: [],
  eventsSent: 0,
  sending: 0,
  recent: {},
  console: null,
};

function randomRef() {
  let out = '';
  for (let i = 0; i < 6; i += 1) {
    out += REF_ALPHABET.charAt(Math.floor(Math.random() * REF_ALPHABET.length));
  }
  return `${out.slice(0, 4)}-${out.slice(4)}`;
}

function getSessionId() {
  if (state.sessionId) return state.sessionId;
  let id = null;
  try {
    id = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${randomRef().replace('-', '')}`;
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
  } catch (e) {
    // Private browsing / storage disabled — fall back to a per-load id.
    id = `nostore-${randomRef().replace('-', '')}`;
  }
  state.sessionId = id;
  return id;
}

function scrubString(value) {
  const trimmed = value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}…[truncated]`
    : value;
  return trimmed
    .replace(EMAIL_PATTERN, '[email]')
    .replace(LONG_DIGITS_PATTERN, '[number]');
}

function redact(value, depth = 0) {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return scrubString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (depth >= MAX_DEPTH) return '[depth]';

  if (Array.isArray(value)) {
    return value.slice(0, 10).map(item => redact(item, depth + 1));
  }

  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).slice(0, 40).forEach((key) => {
      out[key] = SAFE_KEYS.indexOf(key) === -1
        ? '[redacted]'
        : redact(value[key], depth + 1);
    });
    return out;
  }

  return '[unserializable]';
}

/** Strips the origin so query strings and ids stay readable but hosts don't repeat. */
function shortenUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return String(url).slice(0, 200);
  }
}

/**
 * Axios stores the host in `baseURL` and only the path in `url`, so logging
 * `config.url` alone renders CMS, CRM and payment calls indistinguishable —
 * several unrelated endpoints all read as "GET /". The host is what identifies
 * which backend was involved, so it is kept here.
 */
export function describeRequestUrl(config) {
  if (!config) return null;
  const base = config.baseURL || '';
  const relative = config.url || '';
  if (!base && !relative) return null;

  const absolute = /^https?:\/\//i.test(relative)
    ? relative
    : `${base.replace(/\/+$/, '')}/${relative.replace(/^\/+/, '')}`;

  try {
    const parsed = new URL(absolute, window.location.origin);
    return `${parsed.host}${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return String(absolute).slice(0, 200);
  }
}

export function addBreadcrumb(type, message, data) {
  const text = typeof message === 'string' ? message : String(message);
  state.breadcrumbs.push({
    t: new Date().toISOString(),
    type,
    message: scrubString(text.slice(0, MAX_BREADCRUMB_LENGTH)),
    data: data === undefined ? undefined : redact(data),
  });
  if (state.breadcrumbs.length > MAX_BREADCRUMBS) {
    state.breadcrumbs.shift();
  }
}

function isBeaconUrl(url) {
  return typeof url === 'string' && url.indexOf(BEACON_PATH) !== -1;
}

function beaconUrl() {
  const base = (window.processEnv?.VUE_APP_PUBLIC_INTAKE_API_BASE_URL || '')
    .replace(/\/$/, '');
  return base ? `${base}${BEACON_PATH}` : null;
}

function send(payload) {
  const url = beaconUrl();
  if (!url) return;
  // Belt and braces: install() already bails for crawlers, but reportError is
  // imported directly by components and can be reached without it.
  if (isCrawler()) return;

  state.sending += 1;
  const body = JSON.stringify(payload);
  const done = () => { state.sending -= 1; };

  try {
    if (window.fetch) {
      window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).then(done, done);
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onloadend = done;
    xhr.send(body);
  } catch (e) {
    done();
  }
}

/**
 * Records a failure and returns a reference code to show the customer.
 *
 * @param {string} code   Stable identifier, e.g. 'CART_QTY_EXCEEDED'.
 * @param {object} context Free-form detail; passed through redaction.
 * @returns {string} reference code, e.g. 'A7F3-2B'.
 */
export function reportError(code, context = {}) {
  const ref = randomRef();
  const error = context.error;
  const response = error?.response;

  // A failing beacon must never be able to trigger another beacon.
  if (isBeaconUrl(context.message) || isBeaconUrl(error?.config?.url)) return ref;
  if (state.eventsSent >= MAX_EVENTS_PER_SESSION) return ref;

  const dedupeKey = `${code}:${context.message || ''}`;
  const now = Date.now();
  if (state.recent[dedupeKey] && now - state.recent[dedupeKey] < DEDUPE_WINDOW_MS) {
    return ref;
  }
  state.recent[dedupeKey] = now;

  const payload = {
    ref,
    code,
    sessionId: getSessionId(),
    at: new Date().toISOString(),
    url: shortenUrl(window.location.href),
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    message: context.message ? scrubString(String(context.message)) : null,
    stack: error?.stack ? scrubString(String(error.stack)) : null,
    request: response || error?.config ? {
      method: (error?.config?.method || '').toUpperCase() || null,
      url: describeRequestUrl(error?.config),
      requestId: error?.config?.headers?.['X-Request-Id'] || null,
      status: response ? response.status : null,
      statusText: response?.statusText || null,
      body: response?.data ? redact(response.data) : null,
    } : null,
    context: redact(context.data),
    breadcrumbs: state.breadcrumbs.slice(),
  };

  state.eventsSent += 1;
  send(payload);
  addBreadcrumb('error', `${code} (${ref})`);
  return ref;
}

function patchConsole() {
  const original = {
    error: window.console.error.bind(window.console),
    warn: window.console.warn.bind(window.console),
  };
  state.console = original;

  const wrap = (level) => (...args) => {
    original[level](...args);
    try {
      const text = args
        .map(a => (a instanceof Error ? `${a.name}: ${a.message}` : String(a)))
        .join(' ');
      if (isBeaconUrl(text)) return;
      addBreadcrumb(`console.${level}`, text);
    } catch (e) {
      // Never let instrumentation break a log call.
    }
  };

  window.console.error = wrap('error');
  window.console.warn = wrap('warn');
}

/** Wires the global catch-alls. Call once, from main.js, before mounting. */
export function install(Vue, router) {
  if (state.installed) return;
  // Skip the listeners and the console patch entirely for crawlers rather than
  // collecting breadcrumbs we will never send.
  if (isCrawler()) return;
  state.installed = true;

  patchConsole();

  Vue.config.errorHandler = (err, vm, info) => {
    state.console.error(err);
    reportError('VUE_ERROR', {
      message: `${err && err.message} (${info})`,
      error: err,
    });
  };

  // Vue prop/render warnings are noisy and rarely actionable on their own —
  // they ride along as breadcrumbs instead of raising events.
  Vue.config.warnHandler = (msg) => {
    addBreadcrumb('vue.warn', msg);
  };

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    reportError('UNHANDLED_REJECTION', {
      message: reason?.message || String(reason),
      error: reason instanceof Error ? reason : undefined,
    });
  });

  window.addEventListener('error', (event) => {
    // Capture phase: failed <script>/<img>/<link> loads don't bubble to
    // window.onerror. A chunk 404 after a deploy shows up here and nowhere else.
    const target = event.target;
    if (target && target !== window && (target.src || target.href)) {
      const src = target.src || target.href;
      if (isBeaconUrl(src)) return;
      reportError('RESOURCE_LOAD_FAILED', {
        message: `Failed to load ${target.tagName}`,
        data: { url: shortenUrl(src) },
      });
      return;
    }
    reportError('WINDOW_ERROR', {
      message: event?.message,
      error: event?.error,
    });
  }, true);

  if (router) {
    router.afterEach((to) => {
      addBreadcrumb('route', to.fullPath);
    });
  }
}

export default { install, reportError, addBreadcrumb };
